import { useEffect, useState } from "react";
import {
  Phone,
  Mail,
  MapPin,
  Star,
  Leaf,
  Scissors,
  Sprout,
  Hammer,
  Droplets,
  Trees,
  ArrowRight,
  CheckCircle2,
  Facebook,
  Clock,
} from "lucide-react";

const PHOTOS = {
  // striped green lawn — appears first in user-provided photos
  hero: "https://customer-assets.emergentagent.com/job_7373b05a-0e99-4ebd-b73c-ca70f314f717/artifacts/6sarws29_IMG_3060.jpeg",
  // front yard with fresh mulch + boxwood topiaries (house number blurred for privacy)
  about: "/mg-front-yard.jpg",
  // concrete pad / hardscape install in progress with crew
  hardscape:
    "https://customer-assets.emergentagent.com/job_7373b05a-0e99-4ebd-b73c-ca70f314f717/artifacts/0xs6bo10_IMG_3056.jpeg",
  // sod installation on sloped wooded property with crew
  sod: "https://customer-assets.emergentagent.com/job_7373b05a-0e99-4ebd-b73c-ca70f314f717/artifacts/ltkbiiil_IMG_3055.jpeg",
};

const CHARLOTTE_PHONE_DISPLAY = "(516) 323-9848";
const CHARLOTTE_PHONE_TEL = "+15163239848";
const DURHAM_PHONE_DISPLAY = "(919) 885-6532";
const DURHAM_PHONE_TEL = "+19198856532";
const EMAIL = "info@mglawncare.com";

const NAV_LINKS = [
  { label: "Services", href: "#services" },
  { label: "About", href: "#about" },
  { label: "Gallery", href: "#gallery" },
  { label: "Reviews", href: "#reviews" },
  { label: "Contact", href: "#contact" },
];

const SERVICES = [
  {
    icon: Scissors,
    title: "Lawn Mowing & Maintenance",
    desc: "Weekly and bi-weekly mowing, edging and trim that keeps your yard sharp through the season.",
  },
  {
    icon: Sprout,
    title: "Grass Seeding & Sod",
    desc: "New lawn installs, overseeding and full sod replacement — graded, prepped and laid by hand.",
  },
  {
    icon: Leaf,
    title: "Mulching & Pine Straw",
    desc: "Fresh mulch and pine straw installation for clean beds, healthy plants and a finished look.",
  },
  {
    icon: Trees,
    title: "Landscaping Design",
    desc: "Plant selection, garden design and full installation tailored to your property and climate.",
  },
  {
    icon: Hammer,
    title: "Hardscapes & Walkways",
    desc: "Concrete pads, walkways, patios and retaining walls — built tough, finished clean.",
  },
  {
    icon: Droplets,
    title: "Pressure & Soft Washing",
    desc: "Driveways, siding, decks and patios brought back to life with the right pressure for the surface.",
  },
];

const REVIEWS = [
  {
    name: "Verified Google Review",
    rating: 5,
    text: "MG Lawn Care did an incredible job on our front yard. The crew was on time, professional, and the boxwoods + fresh mulch completely transformed the curb appeal. Hands down the best.",
    location: "Charlotte, NC",
  },
  {
    name: "Verified Yelp Review",
    rating: 5,
    text: "Hired them for sod installation on a tricky sloped lot. They prepped, graded, and laid the sod perfectly. Yard looks like a putting green. Fair price, real work ethic.",
    location: "Durham, NC",
  },
  {
    name: "Verified Google Review",
    rating: 5,
    text: "We use MG for weekly lawn maintenance. Stripes are clean every single visit and they always take care of the small details. Highly recommend.",
    location: "Rougemont, NC",
  },
];

const STATS = [
  { value: "5.0", label: "Google Rating", sub: "11+ reviews" },
  { value: "5.0", label: "Yelp Rating", sub: "Verified" },
  { value: "260+", label: "Facebook Followers", sub: "Community trusted" },
  { value: "100%", label: "Locally Operated", sub: "NC owned" },
];

const Stars = ({ count = 5 }) => (
  <div className="inline-flex items-center gap-0.5" aria-label={`${count} out of 5 stars`}>
    {Array.from({ length: count }).map((_, i) => (
      <Star
        key={i}
        className="h-4 w-4 fill-[#C05722] text-[#C05722]"
        strokeWidth={1.5}
      />
    ))}
  </div>
);

const Header = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 16);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <header
      data-testid="site-header"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled
          ? "bg-background/90 backdrop-blur-md border-b border-border"
          : "bg-transparent"
      }`}
    >
      <div className="mx-auto max-w-7xl px-6 lg:px-10 h-16 md:h-20 flex items-center justify-between">
        <a
          href="#top"
          data-testid="header-logo"
          className="flex items-center gap-2 group"
        >
          <span className="inline-flex h-9 w-9 items-center justify-center bg-primary text-primary-foreground font-display font-bold tracking-tight">
            MG
          </span>
          <span
            className={`font-display font-semibold text-base sm:text-lg leading-none ${
              scrolled ? "text-foreground" : "text-white drop-shadow"
            } transition-colors`}
          >
            Lawn Care
            <span className="block text-[10px] sm:text-xs font-normal tracking-[0.2em] uppercase opacity-70">
              & Landscape
            </span>
          </span>
        </a>

        <nav className="hidden md:flex items-center gap-8">
          {NAV_LINKS.map((l) => (
            <a
              key={l.href}
              href={l.href}
              data-testid={`nav-link-${l.label.toLowerCase()}`}
              className={`text-sm font-medium transition-colors hover:text-accent ${
                scrolled ? "text-foreground" : "text-white/90"
              }`}
            >
              {l.label}
            </a>
          ))}
        </nav>

        <div className="flex items-center gap-3">
          <a
            href={`tel:${CHARLOTTE_PHONE_TEL}`}
            data-testid="header-call-now-btn"
            className="inline-flex items-center gap-2 bg-accent text-accent-foreground px-4 sm:px-5 py-2.5 text-sm font-semibold tracking-tight hover:bg-[#A8481B] transition-colors"
          >
            <Phone className="h-4 w-4" strokeWidth={2.4} />
            <span className="hidden sm:inline">{CHARLOTTE_PHONE_DISPLAY}</span>
            <span className="sm:hidden">Call Now</span>
          </a>

          <button
            data-testid="mobile-menu-toggle"
            aria-label="Open menu"
            className={`md:hidden inline-flex h-10 w-10 items-center justify-center ${
              scrolled ? "text-foreground" : "text-white"
            }`}
            onClick={() => setOpen((v) => !v)}
          >
            <div className="flex flex-col gap-1.5">
              <span className="block h-0.5 w-5 bg-current" />
              <span className="block h-0.5 w-5 bg-current" />
              <span className="block h-0.5 w-5 bg-current" />
            </div>
          </button>
        </div>
      </div>

      {open && (
        <div
          data-testid="mobile-menu"
          className="md:hidden bg-background border-t border-border"
        >
          <div className="px-6 py-4 flex flex-col gap-3">
            {NAV_LINKS.map((l) => (
              <a
                key={l.href}
                href={l.href}
                onClick={() => setOpen(false)}
                className="text-foreground text-base py-2"
                data-testid={`mobile-nav-${l.label.toLowerCase()}`}
              >
                {l.label}
              </a>
            ))}
          </div>
        </div>
      )}
    </header>
  );
};

const Hero = () => (
  <section
    id="top"
    data-testid="hero-section"
    className="relative min-h-[92vh] flex items-end overflow-hidden"
  >
    <div className="absolute inset-0">
      <img
        src={PHOTOS.hero}
        alt="A freshly mowed, striped green lawn maintained by MG Lawn Care"
        className="h-full w-full object-cover"
        loading="eager"
      />
      <div className="absolute inset-0 bg-gradient-to-b from-black/55 via-black/30 to-black/75" />
    </div>

    <div className="relative w-full">
      <div className="mx-auto max-w-7xl px-6 lg:px-10 pb-20 pt-32 md:pb-28">
        <div className="max-w-3xl fade-up">
          <div className="inline-flex items-center gap-2 bg-white/10 backdrop-blur-sm border border-white/20 text-white/90 px-3 py-1.5 text-xs tracking-[0.2em] uppercase">
            <Stars />
            <span>5.0 · Google &amp; Yelp</span>
          </div>

          <h1 className="mt-6 font-display font-bold tracking-tight text-white text-balance text-4xl sm:text-5xl lg:text-7xl leading-[1.02]">
            Honest hard work.
            <span className="block text-[#E9ECE5]">
              Beautiful, lasting lawns.
            </span>
          </h1>

          <p className="mt-6 max-w-xl text-base sm:text-lg text-white/85 leading-relaxed">
            MG Lawn Care &amp; Landscape Services is a family-run crew
            delivering lawn care, sod, mulching and hardscape design across
            Charlotte and Durham, NC.
          </p>

          <div className="mt-10 flex flex-col sm:flex-row items-start sm:items-center gap-4">
            <a
              href={`tel:${CHARLOTTE_PHONE_TEL}`}
              data-testid="hero-call-cta"
              className="inline-flex items-center gap-3 bg-accent text-accent-foreground px-7 py-4 text-base font-semibold hover:bg-[#A8481B] transition-colors"
            >
              <Phone className="h-5 w-5" strokeWidth={2.4} />
              Call {CHARLOTTE_PHONE_DISPLAY}
            </a>
            <a
              href="#services"
              data-testid="hero-services-cta"
              className="inline-flex items-center gap-2 text-white border-b border-white/40 hover:border-white pb-1 transition-colors"
            >
              View our services
              <ArrowRight className="h-4 w-4" />
            </a>
          </div>

          <div className="mt-12 flex flex-wrap items-center gap-x-8 gap-y-3 text-white/80 text-sm">
            <div className="flex items-center gap-2">
              <MapPin className="h-4 w-4" />
              Charlotte, NC
            </div>
            <div className="flex items-center gap-2">
              <MapPin className="h-4 w-4" />
              Durham &amp; Rougemont, NC
            </div>
            <div className="flex items-center gap-2">
              <Clock className="h-4 w-4" />
              Open · Closes 9 PM
            </div>
          </div>
        </div>
      </div>
    </div>
  </section>
);

const Marquee = () => (
  <div
    data-testid="trust-marquee"
    className="border-y border-border bg-secondary"
  >
    <div className="mx-auto max-w-7xl px-6 lg:px-10 py-5 flex flex-wrap items-center justify-center md:justify-between gap-x-10 gap-y-3 text-xs sm:text-sm tracking-[0.18em] uppercase text-secondary-foreground/80 font-medium">
      <span>5.0 ★ Google</span>
      <span className="hidden md:inline opacity-30">•</span>
      <span>5.0 ★ Yelp</span>
      <span className="hidden md:inline opacity-30">•</span>
      <span>260+ Facebook Followers</span>
      <span className="hidden md:inline opacity-30">•</span>
      <span>Family Operated</span>
      <span className="hidden md:inline opacity-30">•</span>
      <span>Charlotte · Durham · Rougemont</span>
    </div>
  </div>
);

const Services = () => (
  <section
    id="services"
    data-testid="services-section"
    className="py-24 md:py-32 bg-background"
  >
    <div className="mx-auto max-w-7xl px-6 lg:px-10">
      <div className="grid md:grid-cols-12 gap-10 md:gap-16 items-end mb-16">
        <div className="md:col-span-7">
          <div className="text-xs sm:text-sm uppercase tracking-[0.22em] font-bold text-accent">
            What we do
          </div>
          <h2 className="mt-4 font-display font-semibold tracking-tight text-3xl sm:text-4xl lg:text-5xl text-foreground text-balance">
            Lawn care, landscaping &amp; hardscape — done by a crew that
            actually shows up.
          </h2>
        </div>
        <div className="md:col-span-5 md:pl-6 md:border-l md:border-border">
          <p className="text-muted-foreground text-base sm:text-lg leading-relaxed">
            From weekly mowing to full backyard transformations, every job is
            handled by our own crew. No subcontractors, no shortcuts — just
            clean work, finished on time.
          </p>
        </div>
      </div>

      <div
        className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 border border-border"
        data-testid="services-grid"
      >
        {SERVICES.map((s, idx) => (
          <div
            key={s.title}
            data-testid={`service-card-${idx}`}
            className={`group p-8 md:p-10 bg-background hover:bg-muted/60 transition-colors border-border
              ${idx % 3 !== 2 ? "lg:border-r" : ""}
              ${idx % 2 === 0 ? "sm:border-r lg:border-r" : ""}
              ${idx < SERVICES.length - 1 ? "border-b" : ""}
              ${idx % 3 === 2 ? "lg:border-r-0" : ""}`}
          >
            <div className="inline-flex h-12 w-12 items-center justify-center bg-primary text-primary-foreground mb-6 group-hover:bg-accent transition-colors">
              <s.icon className="h-5 w-5" strokeWidth={2} />
            </div>
            <h3 className="font-display text-xl sm:text-2xl font-semibold text-foreground tracking-tight">
              {s.title}
            </h3>
            <p className="mt-3 text-muted-foreground text-base leading-relaxed">
              {s.desc}
            </p>
          </div>
        ))}
      </div>

      <div className="mt-10 flex flex-wrap items-center gap-x-6 gap-y-2 text-sm text-muted-foreground">
        <span className="font-medium text-foreground">Also offering:</span>
        {[
          "Weed control",
          "Yard work & clean-ups",
          "Garden design",
          "Patios & walkways",
          "Retaining walls",
        ].map((t) => (
          <span key={t} className="inline-flex items-center gap-1.5">
            <CheckCircle2 className="h-4 w-4 text-accent" />
            {t}
          </span>
        ))}
      </div>
    </div>
  </section>
);

const About = () => (
  <section
    id="about"
    data-testid="about-section"
    className="py-24 md:py-32 bg-muted/50"
  >
    <div className="mx-auto max-w-7xl px-6 lg:px-10">
      <div className="grid md:grid-cols-12 gap-10 lg:gap-16 items-center">
        <div className="md:col-span-6 order-2 md:order-1">
          <div className="text-xs sm:text-sm uppercase tracking-[0.22em] font-bold text-accent">
            Our Mission
          </div>
          <h2 className="mt-4 font-display font-semibold tracking-tight text-3xl sm:text-4xl lg:text-5xl text-foreground text-balance">
            Quality lawn care, honest people, and work we&apos;re proud to put
            our name on.
          </h2>
          <p className="mt-6 text-base sm:text-lg text-muted-foreground leading-relaxed">
            Our mission is to offer you quality lawn care specializing in
            hardscape designs through hard work, good customer relations, and
            exceptional work ethic. Whether it&apos;s a weekly mow or a full
            backyard build-out, we treat every property like our own.
          </p>

          <ul className="mt-8 space-y-3">
            {[
              "Family-owned & locally operated in NC",
              "Crew uniformed, insured and on time",
              "Free on-site estimates",
              "Serving Charlotte, Durham & Rougemont",
            ].map((item) => (
              <li
                key={item}
                className="flex items-start gap-3 text-foreground"
              >
                <CheckCircle2 className="h-5 w-5 text-accent mt-0.5 shrink-0" />
                <span className="text-base leading-relaxed">{item}</span>
              </li>
            ))}
          </ul>

          <div className="mt-10 flex flex-col sm:flex-row gap-4">
            <a
              href={`tel:${CHARLOTTE_PHONE_TEL}`}
              data-testid="about-call-cta"
              className="inline-flex items-center justify-center gap-2 bg-primary text-primary-foreground px-6 py-3.5 font-semibold hover:bg-[#0F2417] transition-colors"
            >
              <Phone className="h-4 w-4" />
              Get a Free Estimate
            </a>
            <a
              href={`mailto:${EMAIL}`}
              data-testid="about-email-cta"
              className="inline-flex items-center justify-center gap-2 border border-border bg-background px-6 py-3.5 font-medium hover:bg-secondary transition-colors text-foreground"
            >
              <Mail className="h-4 w-4" />
              Email Us
            </a>
          </div>
        </div>

        <div className="md:col-span-6 order-1 md:order-2 relative">
          <div className="aspect-[4/5] sm:aspect-[5/6] w-full overflow-hidden bg-secondary">
            <img
              src={PHOTOS.about}
              alt="Front-yard landscaping with fresh black mulch and topiary boxwood bushes installed by MG Lawn Care"
              className="h-full w-full object-cover"
              loading="lazy"
            />
          </div>
          <div className="hidden md:block absolute -bottom-6 -left-6 bg-primary text-primary-foreground p-6 max-w-[260px]">
            <div className="font-display text-4xl font-bold leading-none">
              5.0★
            </div>
            <div className="mt-2 text-sm opacity-80 leading-snug">
              Average rating across Google &amp; Yelp from real customers.
            </div>
          </div>
        </div>
      </div>
    </div>
  </section>
);

const Stats = () => (
  <section data-testid="stats-section" className="py-20 md:py-24 bg-primary">
    <div className="mx-auto max-w-7xl px-6 lg:px-10">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-y-10 gap-x-6">
        {STATS.map((s) => (
          <div key={s.label} className="text-primary-foreground">
            <div className="font-display text-5xl sm:text-6xl font-bold tracking-tight">
              {s.value}
            </div>
            <div className="mt-3 text-sm uppercase tracking-[0.2em] font-semibold text-primary-foreground/90">
              {s.label}
            </div>
            <div className="mt-1 text-sm text-primary-foreground/60">
              {s.sub}
            </div>
          </div>
        ))}
      </div>
    </div>
  </section>
);

const Gallery = () => (
  <section
    id="gallery"
    data-testid="gallery-section"
    className="py-24 md:py-32 bg-background"
  >
    <div className="mx-auto max-w-7xl px-6 lg:px-10">
      <div className="max-w-2xl mb-14">
        <div className="text-xs sm:text-sm uppercase tracking-[0.22em] font-bold text-accent">
          Real Projects
        </div>
        <h2 className="mt-4 font-display font-semibold tracking-tight text-3xl sm:text-4xl lg:text-5xl text-foreground text-balance">
          Recent work from our crew.
        </h2>
        <p className="mt-4 text-muted-foreground text-base sm:text-lg leading-relaxed">
          Every photo here is a real MG Lawn Care project — no stock images, no
          filters. Just the results of a hard-working crew.
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-12 gap-4 md:gap-5">
        <figure className="md:col-span-7 md:row-span-2 overflow-hidden group">
          <div className="relative overflow-hidden aspect-[16/12]">
            <img
              src={PHOTOS.hero}
              alt="Lawn mowing project with clean diagonal stripes on a fenced backyard"
              className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-[1.03]"
              loading="lazy"
            />
            <div className="absolute inset-x-0 bottom-0 p-5 md:p-7 bg-gradient-to-t from-black/80 via-black/30 to-transparent text-white">
              <div className="text-xs tracking-[0.2em] uppercase opacity-80">
                Lawn Maintenance
              </div>
              <div className="font-display text-xl sm:text-2xl font-semibold mt-1">
                Weekly mow &amp; striping
              </div>
            </div>
          </div>
        </figure>

        <figure className="md:col-span-5 overflow-hidden group">
          <div className="relative overflow-hidden aspect-[16/11]">
            <img
              src={PHOTOS.hardscape}
              alt="MG Lawn Care crew installing a poured concrete pad with rebar and form boards"
              className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-[1.03]"
              loading="lazy"
            />
            <div className="absolute inset-x-0 bottom-0 p-5 bg-gradient-to-t from-black/80 via-black/30 to-transparent text-white">
              <div className="text-xs tracking-[0.2em] uppercase opacity-80">
                Hardscape
              </div>
              <div className="font-display text-lg sm:text-xl font-semibold mt-1">
                Concrete pad &amp; foundation prep
              </div>
            </div>
          </div>
        </figure>

        <figure className="md:col-span-5 overflow-hidden group">
          <div className="relative overflow-hidden aspect-[16/11]">
            <img
              src={PHOTOS.sod}
              alt="MG Lawn Care crew installing fresh sod on a sloped wooded property"
              className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-[1.03]"
              loading="lazy"
            />
            <div className="absolute inset-x-0 bottom-0 p-5 bg-gradient-to-t from-black/80 via-black/30 to-transparent text-white">
              <div className="text-xs tracking-[0.2em] uppercase opacity-80">
                Sod Installation
              </div>
              <div className="font-display text-lg sm:text-xl font-semibold mt-1">
                Sloped lot sod &amp; grading
              </div>
            </div>
          </div>
        </figure>

        <figure className="md:col-span-12 overflow-hidden group">
          <div className="relative overflow-hidden aspect-[21/9]">
            <img
              src={PHOTOS.about}
              alt="Completed front yard landscaping with fresh mulch and trimmed boxwood topiaries"
              className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-[1.02]"
              loading="lazy"
            />
            <div className="absolute inset-x-0 bottom-0 p-6 md:p-8 bg-gradient-to-t from-black/80 via-black/30 to-transparent text-white">
              <div className="text-xs tracking-[0.2em] uppercase opacity-80">
                Landscape Design
              </div>
              <div className="font-display text-xl sm:text-2xl font-semibold mt-1">
                Front yard mulch + boxwood install
              </div>
            </div>
          </div>
        </figure>
      </div>
    </div>
  </section>
);

const Reviews = () => (
  <section
    id="reviews"
    data-testid="reviews-section"
    className="py-24 md:py-32 bg-secondary grain"
  >
    <div className="mx-auto max-w-7xl px-6 lg:px-10">
      <div className="grid md:grid-cols-12 gap-10 md:gap-16 items-end mb-16">
        <div className="md:col-span-7">
          <div className="text-xs sm:text-sm uppercase tracking-[0.22em] font-bold text-accent">
            What customers say
          </div>
          <h2 className="mt-4 font-display font-semibold tracking-tight text-3xl sm:text-4xl lg:text-5xl text-secondary-foreground text-balance">
            5.0 stars on Google. 5.0 on Yelp. <br className="hidden md:block" />
            Earned, not bought.
          </h2>
        </div>
        <div className="md:col-span-5 flex md:justify-end">
          <div className="flex items-center gap-4">
            <div className="text-right">
              <div className="font-display text-5xl font-bold text-secondary-foreground leading-none">
                5.0
              </div>
              <Stars />
            </div>
            <div className="h-12 w-px bg-secondary-foreground/20" />
            <div>
              <div className="text-sm font-medium text-secondary-foreground">
                Across Google &amp; Yelp
              </div>
              <div className="text-xs text-secondary-foreground/70 mt-1">
                11+ verified reviews · 260+ Facebook followers
              </div>
            </div>
          </div>
        </div>
      </div>

      <div
        className="grid grid-cols-1 md:grid-cols-3 gap-5 md:gap-6"
        data-testid="reviews-grid"
      >
        {REVIEWS.map((r, idx) => (
          <article
            key={idx}
            data-testid={`review-card-${idx}`}
            className="bg-background border border-border p-7 md:p-8 flex flex-col"
          >
            <Stars count={r.rating} />
            <p className="mt-5 text-foreground text-base leading-relaxed flex-1">
              &ldquo;{r.text}&rdquo;
            </p>
            <div className="mt-6 pt-5 border-t border-border">
              <div className="font-medium text-foreground text-sm">
                {r.name}
              </div>
              <div className="text-xs text-muted-foreground mt-0.5">
                {r.location}
              </div>
            </div>
          </article>
        ))}
      </div>

      <div className="mt-12 flex flex-wrap items-center gap-x-6 gap-y-3 text-sm text-secondary-foreground/80">
        <span className="font-medium text-secondary-foreground">
          Find us on:
        </span>
        <a
          href="https://www.google.com/search?q=MG+Lawn+Care+%26+Landscape+Services"
          target="_blank"
          rel="noopener noreferrer"
          data-testid="link-google"
          className="hover:text-accent underline underline-offset-4"
        >
          Google
        </a>
        <a
          href="https://m.yelp.com"
          target="_blank"
          rel="noopener noreferrer"
          data-testid="link-yelp"
          className="hover:text-accent underline underline-offset-4"
        >
          Yelp
        </a>
        <a
          href="https://facebook.com"
          target="_blank"
          rel="noopener noreferrer"
          data-testid="link-facebook"
          className="hover:text-accent underline underline-offset-4"
        >
          Facebook
        </a>
        <a
          href="https://mglandscaping29.wixsite.com"
          target="_blank"
          rel="noopener noreferrer"
          data-testid="link-wix"
          className="hover:text-accent underline underline-offset-4"
        >
          Wix
        </a>
      </div>
    </div>
  </section>
);

const Contact = () => (
  <section
    id="contact"
    data-testid="contact-section"
    className="py-24 md:py-32 bg-primary text-primary-foreground"
  >
    <div className="mx-auto max-w-7xl px-6 lg:px-10">
      <div className="grid md:grid-cols-12 gap-10 lg:gap-16">
        <div className="md:col-span-6">
          <div className="text-xs sm:text-sm uppercase tracking-[0.22em] font-bold text-accent">
            Get in touch
          </div>
          <h2 className="mt-4 font-display font-semibold tracking-tight text-3xl sm:text-4xl lg:text-6xl text-balance">
            Ready for a sharper lawn? Give us a call.
          </h2>
          <p className="mt-6 text-base sm:text-lg text-primary-foreground/80 leading-relaxed max-w-lg">
            Free estimates. No pressure. Tell us what you need — mowing,
            mulching, sod, or a full landscape build — and we&apos;ll get you
            on the schedule.
          </p>

          <div className="mt-10 space-y-5">
            <a
              href={`tel:${CHARLOTTE_PHONE_TEL}`}
              data-testid="contact-charlotte-phone"
              className="group flex items-center gap-5 border-b border-primary-foreground/15 pb-5 hover:border-accent transition-colors"
            >
              <div className="inline-flex h-12 w-12 items-center justify-center bg-accent text-accent-foreground shrink-0">
                <Phone className="h-5 w-5" />
              </div>
              <div className="flex-1">
                <div className="text-xs tracking-[0.2em] uppercase text-primary-foreground/60">
                  Charlotte, NC
                </div>
                <div className="font-display text-2xl sm:text-3xl font-semibold mt-1">
                  {CHARLOTTE_PHONE_DISPLAY}
                </div>
              </div>
              <ArrowRight className="h-5 w-5 opacity-0 group-hover:opacity-100 transition-opacity" />
            </a>

            <a
              href={`tel:${DURHAM_PHONE_TEL}`}
              data-testid="contact-durham-phone"
              className="group flex items-center gap-5 border-b border-primary-foreground/15 pb-5 hover:border-accent transition-colors"
            >
              <div className="inline-flex h-12 w-12 items-center justify-center border border-primary-foreground/30 shrink-0">
                <Phone className="h-5 w-5" />
              </div>
              <div className="flex-1">
                <div className="text-xs tracking-[0.2em] uppercase text-primary-foreground/60">
                  Durham / Rougemont, NC
                </div>
                <div className="font-display text-xl sm:text-2xl font-semibold mt-1">
                  {DURHAM_PHONE_DISPLAY}
                </div>
              </div>
              <ArrowRight className="h-5 w-5 opacity-0 group-hover:opacity-100 transition-opacity" />
            </a>

            <a
              href={`mailto:${EMAIL}`}
              data-testid="contact-email"
              className="group flex items-center gap-5 border-b border-primary-foreground/15 pb-5 hover:border-accent transition-colors"
            >
              <div className="inline-flex h-12 w-12 items-center justify-center border border-primary-foreground/30 shrink-0">
                <Mail className="h-5 w-5" />
              </div>
              <div className="flex-1">
                <div className="text-xs tracking-[0.2em] uppercase text-primary-foreground/60">
                  Email
                </div>
                <div className="font-display text-lg sm:text-xl font-semibold mt-1 break-all">
                  {EMAIL}
                </div>
              </div>
              <ArrowRight className="h-5 w-5 opacity-0 group-hover:opacity-100 transition-opacity" />
            </a>
          </div>
        </div>

        <div className="md:col-span-6">
          <div className="bg-primary-foreground/5 border border-primary-foreground/15 p-8 md:p-10 h-full">
            <h3 className="font-display text-xl sm:text-2xl font-semibold">
              Service areas
            </h3>
            <p className="mt-3 text-primary-foreground/75 text-base leading-relaxed">
              Proudly serving homeowners and businesses across two NC regions:
            </p>

            <div className="mt-8 grid sm:grid-cols-2 gap-6">
              <div className="border-t border-primary-foreground/20 pt-5">
                <div className="text-xs tracking-[0.2em] uppercase text-accent font-semibold">
                  Charlotte Area
                </div>
                <ul className="mt-3 space-y-1.5 text-primary-foreground/85 text-sm">
                  <li>Charlotte</li>
                  <li>Concord</li>
                  <li>Matthews</li>
                  <li>Huntersville</li>
                  <li>Pineville</li>
                </ul>
              </div>
              <div className="border-t border-primary-foreground/20 pt-5">
                <div className="text-xs tracking-[0.2em] uppercase text-accent font-semibold">
                  Triangle Area
                </div>
                <ul className="mt-3 space-y-1.5 text-primary-foreground/85 text-sm">
                  <li>Durham</li>
                  <li>Rougemont</li>
                  <li>Chapel Hill</li>
                  <li>Hillsborough</li>
                  <li>Raleigh (select)</li>
                </ul>
              </div>
            </div>

            <div className="mt-10 pt-6 border-t border-primary-foreground/20">
              <div className="text-xs tracking-[0.2em] uppercase text-primary-foreground/60">
                Business hours
              </div>
              <div className="mt-3 grid grid-cols-2 gap-y-2 text-sm">
                <span className="text-primary-foreground/85">Mon – Sat</span>
                <span className="text-right font-medium">6:00 AM – 9:00 PM</span>
                <span className="text-primary-foreground/85">Sunday</span>
                <span className="text-right font-medium">By appointment</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </section>
);

const Footer = () => (
  <footer
    data-testid="site-footer"
    className="bg-background border-t border-border"
  >
    <div className="mx-auto max-w-7xl px-6 lg:px-10 py-14">
      <div className="grid md:grid-cols-12 gap-10">
        <div className="md:col-span-5">
          <div className="flex items-center gap-2">
            <span className="inline-flex h-9 w-9 items-center justify-center bg-primary text-primary-foreground font-display font-bold">
              MG
            </span>
            <span className="font-display font-semibold text-lg leading-none text-foreground">
              Lawn Care
              <span className="block text-[10px] font-normal tracking-[0.2em] uppercase opacity-70">
                &amp; Landscape Services
              </span>
            </span>
          </div>
          <p className="mt-5 text-muted-foreground text-sm leading-relaxed max-w-sm">
            Quality lawn care specializing in hardscape designs — built on hard
            work, good customer relations, and exceptional work ethic.
          </p>
        </div>

        <div className="md:col-span-3">
          <div className="text-xs tracking-[0.2em] uppercase font-semibold text-foreground">
            Services
          </div>
          <ul className="mt-4 space-y-2 text-sm text-muted-foreground">
            <li>Lawn Mowing</li>
            <li>Mulching &amp; Pine Straw</li>
            <li>Sod Installation</li>
            <li>Hardscapes</li>
            <li>Pressure Washing</li>
          </ul>
        </div>

        <div className="md:col-span-4">
          <div className="text-xs tracking-[0.2em] uppercase font-semibold text-foreground">
            Contact
          </div>
          <ul className="mt-4 space-y-2 text-sm">
            <li>
              <a
                href={`tel:${CHARLOTTE_PHONE_TEL}`}
                data-testid="footer-charlotte-phone"
                className="text-foreground font-medium hover:text-accent"
              >
                {CHARLOTTE_PHONE_DISPLAY} · Charlotte
              </a>
            </li>
            <li>
              <a
                href={`tel:${DURHAM_PHONE_TEL}`}
                data-testid="footer-durham-phone"
                className="text-muted-foreground hover:text-accent"
              >
                {DURHAM_PHONE_DISPLAY} · Durham
              </a>
            </li>
            <li>
              <a
                href={`mailto:${EMAIL}`}
                data-testid="footer-email"
                className="text-muted-foreground hover:text-accent"
              >
                {EMAIL}
              </a>
            </li>
          </ul>

          <div className="mt-6 flex items-center gap-3">
            <a
              href="https://facebook.com"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="footer-facebook"
              aria-label="Facebook"
              className="inline-flex h-10 w-10 items-center justify-center border border-border hover:bg-secondary transition-colors text-foreground"
            >
              <Facebook className="h-4 w-4" />
            </a>
            <a
              href="https://m.yelp.com"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="footer-yelp"
              className="inline-flex h-10 items-center justify-center px-3 border border-border hover:bg-secondary transition-colors text-xs font-semibold tracking-wider text-foreground"
            >
              YELP
            </a>
            <a
              href="https://www.google.com/search?q=MG+Lawn+Care+%26+Landscape+Services"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="footer-google"
              className="inline-flex h-10 items-center justify-center px-3 border border-border hover:bg-secondary transition-colors text-xs font-semibold tracking-wider text-foreground"
            >
              GOOGLE
            </a>
          </div>
        </div>
      </div>

      <div className="mt-12 pt-6 border-t border-border flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 text-xs text-muted-foreground">
        <div>
          © {new Date().getFullYear()} MG Lawn Care &amp; Landscape Services,
          LLC. All rights reserved.
        </div>
        <div>Serving Charlotte · Durham · Rougemont, NC</div>
      </div>
    </div>
  </footer>
);

const Home = () => (
  <div className="bg-background text-foreground" data-testid="home-page">
    <Header />
    <main>
      <Hero />
      <Marquee />
      <Services />
      <About />
      <Stats />
      <Gallery />
      <Reviews />
      <Contact />
    </main>
    <Footer />
  </div>
);

export default Home;
